import { useMemo } from 'react';

import { getFormattedPercentage } from '@lyearn/core/utils/number/getFormattedPercentage';
import { Trans, useTranslation } from '@lyearn/i18n';
import { Stack, Typography } from '@lyearn/ui';
import QuizSettingsEntity from '@/modules/corporate/modules/quiz/entity/QuizSettings';
import { QuizResultEnum } from '@/types/schema';

import { getStatus } from '../../../../utils';
import { getQuizPassingPercentage } from '../../../QuizEditor/utils';
import getModifiedAttempts from '../../utils/getQuizModifiedAttempts';
import { QuizDetailsActionProps } from '../QuizDetailsAction/types';
import { getBestScore } from '../QuizDetailsAction/utils';

const components = {
  labelSecondary: <Typography color="text-text-secondary" variant="label-02" />,
};

type Props = Pick<QuizDetailsActionProps, 'content' | 'quizPerformance'>;

const ScoreAndAttemptsInfo = (props: Props) => {
  const { t } = useTranslation();
  const { content, quizPerformance } = props;

  const maxAttempts = QuizSettingsEntity.getAllowedAttempts(content.settings);
  const passingPercentage = getQuizPassingPercentage(content.passingPercentage);

  const maxScore = quizPerformance?.maxSubmissionDetails?.maxScore ?? content.maxScore;
  const attemptsCount = useMemo(
    () => getModifiedAttempts(quizPerformance).length,
    [quizPerformance],
  );

  const bestScore = getBestScore(quizPerformance);
  const bestScorePercentage = (bestScore / maxScore) * 100;
  const status = useMemo(
    () => getStatus({ quiz: content, performance: quizPerformance, t }),
    [content, quizPerformance, t],
  );

  return (
    <Stack className="mt-32 flex-wrap" gapX={32} gapY={24}>
      {status ? (
        <>
          <Stack direction="col">
            <Typography color="text-text-secondary" variant="label-02">
              {t('QuizPlayer:Result')}
            </Typography>
            <Typography color="text-text-primary" variant="heading-xs">
              {status.result === QuizResultEnum.Pass
                ? t('Common:ContentStatus.Pass')
                : t('Common:ContentStatus.Fail')}
            </Typography>
          </Stack>
          {/* <Stack direction="col">
            <Typography color="text-text-secondary" variant="label-02">
              {t('QuizPlayer:YourBestScore')}
            </Typography>
            <Typography color="text-text-primary" variant="heading-xs">
              {bestScore}/{maxScore}
            </Typography>
          </Stack> */}
          <Stack direction="col">
            <Typography color="text-text-secondary" variant="label-02">
              {t('QuizPlayer:YourBestScore')}
            </Typography>
            <Typography color="text-text-primary" variant="heading-xs">
              {getFormattedPercentage(bestScorePercentage)}
            </Typography>
          </Stack>
        </>
      ) : (
        <>
          {/* <Stack direction="col">
            <Typography color="text-text-secondary" variant="label-02">
              {t('QuizPlayer:MaxScore')}
            </Typography>
            <Typography color="text-text-primary" variant="heading-xs">
              {maxScore}
            </Typography>
          </Stack> */}
        </>
      )}
      <Stack direction="col">
        <Typography color="text-text-secondary" variant="label-02">
          {t('QuizPlayer:PassCutoff')}
        </Typography>
        <Typography color="text-text-primary" variant="heading-xs">
          {passingPercentage}%
        </Typography>
      </Stack>
      <Stack direction="col">
        <Typography color="text-text-secondary" variant="label-02">
          {t('QuizPlayer:Attempts')}
        </Typography>
        <Typography color="text-text-primary" variant="heading-xs">
          <Trans
            color="text-text-secondary"
            i18nKey="QuizPlayer:BeforeAttemptsInfo"
            values={{ count: attemptsCount, max: maxAttempts }}
            variant="heading-xs"
          />
        </Typography>
      </Stack>
    </Stack>
  );
};

export default ScoreAndAttemptsInfo;
